import type { CSSProperties } from '@linaria/core';
import { cx } from '@linaria/core';
import { SyntheticEvent, memo, useCallback, useMemo, useState } from 'react';
import { openMenuDialog } from '../../../../actions/menu';
import { goToNotes } from '../../../../actions/nav';
import { SettingsType } from '../../../../constants/enums';
import locales from '../../../../locales/en';
import { TransitionTypes } from '../../../../state';
import { openRoom, pinRoom, setLoungeSurrogate } from '../../../../state/state.rooms';
import { isTouchDevice } from '../../../../utilities/browser';
import { motionTransition } from '../../../../utilities/transitions';
import ButtonModelMenu from '../../../Buttons/ButtonModelMenu';
import { useDragHelper } from '../../../Hooks/useDragHelper';
import { useIsRoomPinned } from '../../../Hooks/useRoom';
import { useSettingSelector } from '../../../Hooks/useSettings';
import { useUserSelector } from '../../../Hooks/useUser';
import RoomIcon from '../../../Icons/Room';
import IconViewers from '../../../Icons/Viewers';
import { palette } from '../../../styles/colors';
import BookmarkBadge from '../Badges/Bookmark';
import FriendBadge from '../Badges/Friend';
import MissMFCBadge from '../Badges/MissMFC';
import NewBadge from '../Badges/New';
import PopularBadge from '../Badges/Popular';
import PriorMissMFCBadge from '../Badges/PriorMissMFC';
import RecommendationBadge from '../Badges/Recommendation';
import StatusBadge from '../Badges/Status';
import TrendingBadge from '../Badges/Trending';
import ClubShowBanner from '../Banners/ClubShow';
import GroupShowBanner from '../Banners/GroupShow';
import MissMFCBanner from '../Banners/MissMFC';
import NewModelBanner from '../Banners/NewModel';
import PopularBanner from '../Banners/Popular';
import PrivateShowBanner from '../Banners/PrivateShow';
import TrendingBanner from '../Banners/Trending';
import NotesButton from '../Buttons/NotesButton';
import PinButton from '../Buttons/PinButton';
import Container from '../Container';
import LargeFloatingPreview from '../LargeFloatingPreview';
import ModelName from '../ModelName';
import { GetStylesConfig, getAnonymousMissMFCRank, isAnonymousMissMFC, resolveDisplayHighlight, resolveModelStatus } from '../helpers';
import "./Text.linaria.module.css!=!./Text?./Text.linaria.module.css?css=H4sIAAAAAAAAA3VV3bKzKBC836c4N1u1X9V6StREj/swFAooEcEAaoy1774VfwiQ/e5s6HF6egb4RqaHKYVGdKRL10oqTFQ5IfVXFHG0EBVx1rQm2jd+/fPvH9+viMsNsmQ2WXZERAphNuryOjxOSoIhmG/xrZIBXCtUd42So8BRLbk80znLRiGh+VgTYWxKWkPQXuVtfAZwvY3aMLpEtRSGCFO+wog645CCoBoy2dUBXCknjwgzRWrDpChrycdenGF5DBPKWRqvlXxEukVYzp4vUpBj2UrsO9gmfX+7roPUbPupIhwZNpGT0npFWLQizhoRMUN6HejvNASX6T7PapUTUZTLuWwZxsRK/dEQmMfMJr5ipgeOlvJV2rlNEgikvstkWlvyamYJ4vjPczelENBeDRd8Nh8Mjy8tOcNfe7kta9ptCP7+2voyIBX2ZakywYcAfvRFD6gmUUXMTN7iX+pwd7kzdKo7sm7AZpm4dWFimlWMM7MEPkyLLXRm2LRendccVh2J0+6cWCOHj1KxQjNREWYTc6f9VVMFpunSB/A3JaJtit8tJ3pMm9EDXsO3QSTviJxDNi2Y5+tvzociE1GaWIF1AUGaE3pLArhSKUyk2ZMcP7H4HathIVKQVD7aI+e9JZXkVhyXkOGBNpmP1gFhzESz+RpbMvHIJCRX0hjZO/wOLm0WV9RHls8JNWXyvmQ49/g85KtNvhMwTRDcf+q6ea5zywyJto6VQs4KDfbIZfD6wBhXqxxQ/Rq0+Luw7UzdlBatPVINE1v5Tr42dg2w6GTv+mz5zROCeWy0uATw5G/1o9FIxzDX4C40eAuIHbtcNg/ZgZxpgeo5GpUc52kfoe37lyPZscMiT7DbsA4CnlcpUgH0Fadei70I/hGxq3ZCGgZBAeJrXh8yDuU7sNITDEUucXHz0ef7NApNjHvLHE3ZXfHaISE1DeOJj7zDUTi1EY9OQvpxPArPPzeiCyM29wrPPZfPQ/7unRMwcUhmyi+Ve89un9w+Ys0dJoTeL8ZHZ88P1a7nx5o7NQD1/bUpAujNjfeH14r/6Fz7uGkC+HEjb3erNkgZ50iCH0Cf9zyA/qH0kivvMaK1e6Vb9P+5nXv9lWrJMslpAP3M2daN/wCCBUfimgkAAA=="
const rootStyles = "atm_3f_tnkek3 atm_5j_i2wt44 atm_2d_1wj0jbo atm_fc_1h6ojuz atm_ar_1bp4okc atm_70_2fli30 atm_mk_h2mmj6 atm_h_1h6ojuz atm_ks_15vqwwr atm_9s_1txwivl atm_e2_1osqo2v";
const stylesRootHighlight = "atm_3f_1fmrp5d";
const textContainerStyles = "atm_fc_1yb4nlp atm_e2_1dk5qia atm_vl_15vqwwr atm_9s_1txwivl atm_vy_1osqo2v";
const stylesNewBadgeContainer = "atm_fc_1h6ojuz atm_vl_15vqwwr atm_9s_1txwivl atm_vy_1osqo2v";
const bannerContainerStyles = "atm_67_bke03k atm_e2_1dk5qia atm_vl_15vqwwr atm_vy_1osqo2v";
const statusStyles = "atm_fc_1b1vv5m atm_e2_1dk5qia atm_h_1h6ojuz atm_vl_15vqwwr atm_9s_1txwivl atm_vy_1osqo2v";
const stylesBadgeContainer = "atm_fc_1yb4nlp atm_e2_1dk5qia atm_h_esu3gu atm_vl_15vqwwr atm_9s_1txwivl atm_vy_1osqo2v";
const stylesMissMfcContainer = "atm_7l_ivydl7 atm_c8_137efj2 atm_fc_1h6ojuz atm_h_1h6ojuz atm_cs_8n312b atm_lo_idpfg4 atm_le_idpfg4 atm_lk_yh40bf atm_ll_yh40bf atm_vv_1q9ccgz atm_9s_1txwivl atm_k4_6xdddb atm_h3_yh40bf";
const stylesMissMfcContainerCompact = "atm_h0_idpfg4";
const stylesMarginLeft = "atm_gz_1wugsn5";
const stylesNewBadge = "atm_lo_idpfg4 atm_le_idpfg4 atm_lk_idpfg4 atm_ll_idpfg4";
const stylesPriorMissMFCBadgeClass = "atm_e2_1dk5qia atm_vy_rzutr2 atm_gz_yh40bf";
const stylesHeightAndFontSizeAndPaddingDefault = "atm_c8_137efj2 atm_e2_1dk5qia atm_lo_idpfg4 atm_le_idpfg4 atm_lk_1l7b3ar atm_ll_1l7b3ar";
const stylesHeightAndFontSize = "atm_c8_137efj2 atm_e2_1dk5qia";
const stylesMargin = "atm_gi_181067c";
const stylesNewModelBanner = "atm_7l_ivydl7 atm_c8_137efj2 atm_e2_1dk5qia";
const stylesStatusBadge = "atm_7l_ivydl7 atm_c8_137efj2 atm_2d_n7od8j atm_ks_15vqwwr atm_vy_1wugsn5";
const stylesContainer = "atm_lo_ftgil2 atm_le_ftgil2 atm_lk_ftgil2 atm_ll_ftgil2";
// const stylesButtonModelMenu = css`
//     height: var(--height);
//     align-items: center;
//     visibility: hidden;
//     display: flex;
// `
const stylesVisibility = "atm_vl_ewfl5b";
const stylesDisplayMissMFCBadge = "atm_9s_1txwivl";
const stylesBadgesContainer = "atm_gq_2efq5t atm_gz_1amm6g8 atm_ar_1bp4okc atm_9s_1txwivl";
const stylesBadges = "atm_fc_1h6ojuz atm_9s_1txwivl";
const stylesFlexStart = "atm_fc_1y6m0gg";
const stylesModelName = "atm_c8_137efj2 atm_fc_1h6ojuz atm_e2_1dk5qia atm_h_1h6ojuz atm_le_idpfg4 atm_vy_1osqo2v";
const stylesMenuViewers = "atm_gq_2efq5t atm_h0_191fzq7 atm_fc_esu3gu atm_ar_1bp4okc atm_9s_1txwivl";
const stylesViewerContainer = "atm_7l_ivydl7 atm_h_1h6ojuz atm_h0_1y44olf atm_cs_8n312b atm_9s_1txwivl";
const stylesViewerCounter = "atm_c8_137efj2 atm_gz_yh40bf";
interface TextProps {
  activeRoom?: number;
  columnWidth: number;
  disableMenuNextPrev?: boolean;
  displayBookmarkBadge?: boolean;
  displayBookmarkHighlight?: boolean;
  displayFriendBadge?: boolean;
  displayFriendHighlight?: boolean;
  displayGroupShowBanner?: boolean;
  displayPrivateShowBanner?: boolean;
  displayClubShowBanner?: boolean;
  displayMissMFCBadge?: boolean;
  displayMissMFCBanner?: boolean;
  displayNewModelBadge?: boolean;
  displayNewModelBanner?: boolean;
  displayNoteIconButton?: boolean;
  displayPopularBadge?: boolean;
  displayPopularBanner?: boolean;
  displayPopularHighlight?: boolean;
  displayRecentlyVisitedBadge?: boolean;
  displayRecentlyVisitedHighlight?: boolean;
  displayRecommendationBadge?: boolean;
  displayRecommendationHighlight?: boolean;
  displayStatusBadge?: boolean;
  displayTrendingBadge?: boolean;
  displayTrendingBanner?: boolean;
  displayTrendingHighlight?: boolean;
  enableLargePreview?: boolean;
  enablePin?: boolean;
  enablePinToTopSymbols?: boolean;
  getStyle: (key: string, config?: GetStylesConfig) => any;
  gridPadding: number;
  id: number;
  loungeSlug?: string;
  name: string;
  singlePin?: boolean;
  pinLimitReached?: boolean;
}
const Text = memo((props: TextProps) => {
  const {
    displayRecentlyVisitedHighlight,
    displayRecentlyVisitedBadge,
    displayTrendingHighlight,
    displayRecommendationBadge,
    displayRecommendationHighlight,
    displayBookmarkHighlight,
    displayPopularHighlight,
    displayFriendHighlight,
    displayGroupShowBanner,
    displayPrivateShowBanner,
    displayClubShowBanner,
    displayTrendingBanner,
    displayNewModelBanner,
    displayPopularBanner,
    displayMissMFCBanner,
    displayBookmarkBadge,
    displayTrendingBadge,
    displayNewModelBadge,
    displayPopularBadge,
    displayMissMFCBadge,
    displayStatusBadge,
    displayFriendBadge,
    enableLargePreview,
    displayNoteIconButton,
    enablePinToTopSymbols,
    disableMenuNextPrev,
    pinLimitReached,
    gridPadding,
    columnWidth,
    activeRoom,
    loungeSlug,
    singlePin,
    enablePin,
    getStyle,
    name,
    id
  } = props;
  const {
    dragStart,
    dragMove,
    dragEnd
  } = useDragHelper(id);
  const [sort] = useSettingSelector(SettingsType.DEFAULT, 'sort');
  const [showMenu, setShowMenu] = useState(false);
  const isPinnedToTopRecentlyVisited = useUserSelector(id, 'isPinnedToTopRecentlyVisited');
  const isPinnedToTopTrending = useUserSelector(id, 'isPinnedToTopTrending');
  const isPinnedToTopPopular = useUserSelector(id, 'isPinnedToTopPopular');
  const isPinnedToTopMissMFC = useUserSelector(id, 'isPinnedToTopMissMFC');
  const isBanned = useUserSelector(id, 'banned');
  const isGroupShowPending = useUserSelector(id, 'isGroupShowPending');
  const priorMissMfcRank = useUserSelector(id, 'priorMissMfcRank');
  const groupShowRoomCount = useUserSelector(id, 'groupShowRoomCount');
  const isTruePrivate = useUserSelector(id, 'isTruePrivate');
  const isTrendingNew = useUserSelector(id, 'isTrendingNew');
  const isRecommended = useUserSelector(id, 'isPinnedToTopModelYouMayLike');
  const isPrivateShow = useUserSelector(id, 'isPrivateShow');
  const trendingDiff = useUserSelector(id, 'trendingDiff');
  const missMFCPrize = useUserSelector(id, 'missMFCPrize');
  const isPublicShow = useUserSelector(id, 'isPublicShow');
  const isGroupShow = useUserSelector(id, 'isGroupShow');
  const isAnonymous = isAnonymousMissMFC(id);
  const missMFCRankFromSelector = useUserSelector(id, 'missMFCRank');
  const missMFCRank = isAnonymous ? getAnonymousMissMFCRank(name) : missMFCRankFromSelector;
  const isMissMFCFromSelector = useUserSelector(id, 'isMissMFC');
  const isMissMFC = isAnonymous ? true : isMissMFCFromSelector;
  const isBookmark = useUserSelector(id, 'isBookmark');
  const isNewModel = useUserSelector(id, 'isNewModel');
  const isClubShow = useUserSelector(id, 'isClubShow');
  const isPopular = useUserSelector(id, 'isPopular');
  const isOffline = useUserSelector(id, 'isOffline');
  const isKicked = useUserSelector(id, 'isKicked');
  const isFriend = useUserSelector(id, 'isFriend');
  const creation = useUserSelector(id, 'creation');
  const viewers = useUserSelector(id, 'viewers');
  const hasNote = useUserSelector(id, 'hasNote');
  const isAway = useUserSelector(id, 'isAway');
  const color = useUserSelector(id, 'color');
  const slug = useUserSelector(id, 'slug');

  // const stylesUtilitiesPX = getStyle('utilities', { px: true })
  // const stylesFooterPX = getStyle('footer', { px: true })
  const stylesFooter = getStyle('footer');
  // const stylesBannerPX = getStyle('banner', { px: true })
  const stylesName = getStyle('name');
  const stylesUtilities = getStyle('utilities');
  const stylesBanner = getStyle('banner');
  const stylesBannerPopular = stylesBanner('popular');
  const stylesBannerPopularViewers = stylesBannerPopular('viewers');
  const stylesBannerTrending = stylesBanner('trending');
  const stylesBannerTrendingDiff = stylesBannerTrending('diff');
  const stylesBannerNew = stylesBanner('new');
  const stylesBannerNewTime = stylesBannerNew('time');
  const stylesBannerMissMFC = stylesBanner('missMFC');
  const stylesBannerMissMFCRank = stylesBannerMissMFC('rank');
  const stylesBadge = getStyle('badge');
  const stylesBadgeStatus = stylesBadge('status');
  const stylesBadgeMissMFC = stylesBadge('missMFC');
  const stylesPriorMissMFCBadge = stylesBadge('priorMissMFC');
  const stylesBadgeNew = stylesBadge('new');
  const stylesSection1 = getStyle('section1');
  const modelStatus = resolveModelStatus({
    isTruePrivate,
    isPrivateShow,
    isGroupShow,
    isClubShow,
    isAway
  });
  const onOpenMenuCallback = useCallback(() => {
    openMenuDialog(slug, {
      hideNextPrev: disableMenuNextPrev,
      transitionSource: TransitionTypes.TILE
    });
  }, [slug, disableMenuNextPrev]);
  const onOpenContextMenuCallback = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    event.preventDefault();
    if (isAnonymous) return;
    onOpenMenuCallback();
    return false;
  }, [isAnonymous, onOpenMenuCallback]);
  const onGoToRoomCallback = useCallback((event: SyntheticEvent) => {
    console.log('[Text] onGoToRoomCallback', id, event.target);
    event.stopPropagation();
    if (isAnonymous) return;
    if (loungeSlug) {
      // if we're in the lounge, set the surrogate
      setLoungeSurrogate(id, loungeSlug);
    } else {
      openRoom(slug, {
        transitionSource: TransitionTypes.TILE
      });
    }
  }, [isAnonymous, id, slug, loungeSlug]);
  const isRoomPinned = useIsRoomPinned(id);

  // if (!id) {
  //     return <AvatarSkeleton width={width} height={height} />
  // }

  // const modelColor = useModelColor(color)

  //
  // For testing purposes:
  //
  // const candisplayFriendBadge = true
  // const canDisplayBookmarkBadge = true
  // const canDisplayTrendingBadge = true
  // const canDisplayPopularBadge = true
  // const canDisplayStatusBadge = true
  // const canDisplayNewModelBadge = true
  // const canDisplayMissMFCBadge = true
  // const canDisplayPriorMissMFCBadge = true
  // const canDisplayRecentlyVisitedBadge = true
  // const canDisplayNotesButton = false
  // const canEnablePinToTopSymbols = false
  // const canDisplayLargePreviewButton = true
  // const canDisplayPinButton = true

  //
  // Symbols
  //
  const canEnablePinToTopSymbols = enablePinToTopSymbols;
  const dicanDisplayGroupShowBanner = displayGroupShowBanner && (isGroupShow || isGroupShowPending || groupShowRoomCount);
  const canDisplayPrivateShowBanner = displayPrivateShowBanner && (isPrivateShow || isTruePrivate);
  const canDisplayClubShowBanner = displayClubShowBanner && isClubShow;
  const canDisplayTrendingBanner = displayTrendingBanner && isTrendingNew && trendingDiff;
  const canDisplayNewModelBanner = displayNewModelBanner && isNewModel;
  const canDisplayPopularBanner = displayPopularBanner && isPopular && viewers;
  const canDisplayMissMFCBanner = displayMissMFCBanner && isMissMFC && missMFCRank;
  const candisplayFriendBadge = displayFriendBadge && isFriend;
  const canDisplayBookmarkBadge = displayBookmarkBadge && isBookmark;
  const canDisplayTrendingBadge = displayTrendingBadge && (!canEnablePinToTopSymbols || isPinnedToTopTrending) && isTrendingNew && trendingDiff;
  const canDisplayPopularBadge = displayPopularBadge && (!canEnablePinToTopSymbols || isPinnedToTopPopular) && isPopular && viewers;
  const canDisplayNewModelBadge = displayNewModelBadge && isNewModel;
  const canDisplayMissMFCBadge = displayMissMFCBadge && (!canEnablePinToTopSymbols || isPinnedToTopMissMFC || sort === 'miss_mfc') && isMissMFC && missMFCRank > 0;
  const canDisplayPriorMissMFCBadge = !!priorMissMfcRank;
  const canDisplayStatusBadge = displayStatusBadge && !!modelStatus;
  const canDisplayRecentlyVisitedBadge = displayRecentlyVisitedBadge && (!canEnablePinToTopSymbols || isPinnedToTopRecentlyVisited);

  //
  // Other
  //
  // const canShowMenu = !isAnonymous && (isTouchDevice || showMenu)
  const canShowMenu = !isAnonymous && (isTouchDevice || showMenu);
  const canShowViewers = !!viewers && !canDisplayPopularBanner;
  const availableWidth = columnWidth - gridPadding;
  const inHiddenShow = isTruePrivate || isPrivateShow || isGroupShow || isClubShow;
  const noShowType = isOffline || inHiddenShow || isAway;
  const isViewable = !noShowType && !isBanned && !isKicked;
  const showSinglePin = singlePin && isViewable;

  //
  // Buttons
  //
  const canDisplayNotesButton = hasNote && displayNoteIconButton;
  const canDisplayLargePreviewButton = enableLargePreview && isPublicShow && !isAnonymous;
  const canDisplayPinButton = (enablePin && !pinLimitReached || isRoomPinned || showSinglePin) && !isAnonymous;
  //
  // Highlights
  //
  const canDisplayActiveRoomHighlight = activeRoom === id;
  const canDisplayFriendHighlight = displayFriendHighlight && isFriend;
  const canDisplayBookmarkHighlight = displayBookmarkHighlight && isBookmark;
  const canDisplayPopularHighlight = displayPopularHighlight && isPopular && (!canEnablePinToTopSymbols || isPinnedToTopPopular);
  const canDisplayTrendingHighlight = displayTrendingHighlight && isTrendingNew && (!canEnablePinToTopSymbols || isPinnedToTopTrending);
  const canDisplayRecentlyViewedHighlight = displayRecentlyVisitedHighlight && (!canEnablePinToTopSymbols || isPinnedToTopRecentlyVisited);
  const canDisplayRecommendationBadge = displayRecommendationBadge && (!canEnablePinToTopSymbols || isRecommended);
  const canDisplayRecommendationHighlight = displayRecommendationHighlight && isRecommended && (!canEnablePinToTopSymbols || isRecommended);
  const highlight = resolveDisplayHighlight({
    displayRecentlyVisitedHighlight: canDisplayRecentlyViewedHighlight,
    displayActiveRoomHighlight: canDisplayActiveRoomHighlight,
    displayTrendingHighlight: canDisplayTrendingHighlight,
    displayBookmarkHighlight: canDisplayBookmarkHighlight,
    displayPopularHighlight: canDisplayPopularHighlight,
    displayFriendHighlight: canDisplayFriendHighlight,
    displayRecommendationHighlight: canDisplayRecommendationHighlight
  });
  const stylesViewers = getStyle('viewers');
  const onPinCallback = useCallback((event: SyntheticEvent) => {
    event.stopPropagation();
    motionTransition(() => pinRoom(id, {
      pinned: !isRoomPinned
    }), {
      id,
      skipTransition: isRoomPinned,
      // skip if we're unpinning
      transitionSource: TransitionTypes.TILE,
      transitionDestination: TransitionTypes.PINROOM
    });
  }, [id, isRoomPinned]);
  const onOpenNote = useCallback(event => {
    event.stopPropagation();
    goToNotes(name);
  }, [id, name]);
  const canDisplayBanners = dicanDisplayGroupShowBanner || canDisplayPrivateShowBanner || canDisplayClubShowBanner || canDisplayTrendingBanner || canDisplayNewModelBanner || canDisplayMissMFCBanner || canDisplayPopularBanner;
  const canDisplayBadges = canDisplayBookmarkBadge || canDisplayTrendingBadge || canDisplayMissMFCBadge || canDisplayPopularBadge || canDisplayStatusBadge || candisplayFriendBadge || canDisplayRecommendationBadge;
  const canDisplayUtilities = canDisplayRecentlyVisitedBadge || canDisplayLargePreviewButton || canDisplayNotesButton || canDisplayPinButton;
  const badgeCount = useMemo(() => [Number(canDisplayBookmarkBadge), Number(canDisplayTrendingBadge), Number(canDisplayPopularBadge), Number(candisplayFriendBadge)].reduce((memo, active) => memo + active, 0), [canDisplayTrendingBadge, canDisplayBookmarkBadge, canDisplayPopularBadge, candisplayFriendBadge]);
  const buttonCount = useMemo(() => [Number(canDisplayRecentlyVisitedBadge), Number(canDisplayLargePreviewButton), Number(canDisplayNotesButton), Number(canDisplayPinButton)].reduce((memo, active) => memo + active, 0), [canDisplayRecentlyVisitedBadge, canDisplayLargePreviewButton, canDisplayNotesButton, canDisplayPinButton]);
  return <div data-testid="Container__ModelDisplay" id={`tile-${id}`} key={id} className={cx(rootStyles, highlight && stylesRootHighlight)} style={highlight && {
    '--highlight': highlight
  } as CSSProperties} onMouseLeave={isTouchDevice ? null : () => setShowMenu(false)} onMouseOver={isTouchDevice ? null : () => setShowMenu(true)} onContextMenu={onOpenContextMenuCallback} onClick={onGoToRoomCallback} onDragStart={dragStart} onDragEnd={dragEnd} onDrag={dragMove} draggable>
            <div data-testid="Container__Text" style={{
      '--height': stylesUtilities('height') + 'px'
    } as CSSProperties} className={cx(textContainerStyles, canDisplayUtilities && stylesVisibility)}>
                {canDisplayLargePreviewButton && <LargeFloatingPreview shadow={false} color={palette.backgroundReverse} size={stylesUtilities('iconSize')} id={id} />}
                {canDisplayNotesButton && <NotesButton size={stylesUtilities('iconSize')} color={palette.backgroundReverse} filled onClick={onOpenNote} title="Open Notes" shadow={false} />}
                {canDisplayRecentlyVisitedBadge && <Container className={stylesContainer} title="Recently Visited" shadow={false}>
                        <RoomIcon size={stylesUtilities('iconSize')} color={palette.backgroundReverse} filled />
                    </Container>}
                {canDisplayPinButton && <PinButton title={(isRoomPinned ? 'Unpin' : 'Pin') + ' Model'} className={cx(buttonCount === 1 && stylesMarginLeft)} size={stylesUtilities('iconSize')} color={palette.backgroundReverse} onClick={onPinCallback} filled={isRoomPinned} shadow={false} />}
            </div>
            <div style={{
      '--font-size': stylesBadgeStatus('fontSize') + 'px',
      '--height': stylesBadgeNew('height') + 'px'
    } as CSSProperties} className={cx(stylesNewBadgeContainer, stylesHeightAndFontSize, canDisplayNewModelBadge && stylesVisibility)}>
                <NewBadge className={stylesNewBadge} />
            </div>
            <ModelName modelId={id} style={{
      '--font-size': stylesName('fontSize')() + 'px',
      '--height': stylesName('height') + 'px'
    } as CSSProperties} className={stylesModelName} isAnonymous={isAnonymous} color={color} name={name} />
            <div style={{
      '--height': stylesBanner('height') + 'px'
    } as CSSProperties} className={cx(bannerContainerStyles, canDisplayBanners && stylesVisibility)}>
                {canDisplayMissMFCBanner && <MissMFCBanner className={stylesHeightAndFontSizeAndPaddingDefault} style={{
        '--font-size': stylesBanner('fontSize') + 'px',
        '--height': stylesBanner('height') + 'px'
      }} rankClassName={stylesMargin} rankStyle={{
        '--margin': stylesBannerMissMFCRank('margin')
      }} text={stylesBannerMissMFC('text')} missMFCPrize={missMFCPrize} missMFCRank={missMFCRank} />}
                {canDisplayTrendingBanner && <TrendingBanner className={stylesHeightAndFontSize} style={{
        '--font-size': stylesBanner('fontSize') + 'px',
        '--height': stylesBanner('height') + 'px'
      }} diffClassName={stylesMargin} diffStyle={{
        '--margin': stylesBannerTrendingDiff('margin')
      }} text={stylesBannerTrending('text')} hideIcon={availableWidth < stylesBanner('minimumWidth')} diff={trendingDiff} />}
                {canDisplayPopularBanner && <PopularBanner className={stylesHeightAndFontSize} style={{
        '--font-size': stylesBanner('fontSize') + 'px',
        '--height': stylesBanner('height') + 'px'
      }} viewersClassName={stylesMargin} viewersStyle={{
        '--margin': stylesBannerPopularViewers('margin')
      }} iconSize={stylesBanner('iconSize')} text={stylesBannerPopular('text')} hideIcon={availableWidth < stylesBanner('minimumWidth')} viewers={viewers} />}
                {dicanDisplayGroupShowBanner && (isGroupShow || isGroupShowPending || groupShowRoomCount) && <GroupShowBanner className={stylesHeightAndFontSizeAndPaddingDefault} style={{
        '--font-size': stylesBanner('fontSize') + 'px',
        '--height': stylesBanner('height') + 'px'
      }} iconSize={stylesBanner('iconSize')} roomCount={groupShowRoomCount} isLive={isGroupShow} />}
                {canDisplayPrivateShowBanner && (isPrivateShow || isTruePrivate) && <PrivateShowBanner className={stylesHeightAndFontSizeAndPaddingDefault} style={{
        '--font-size': stylesBanner('fontSize') + 'px',
        '--height': stylesBanner('height') + 'px'
      }} iconSize={stylesBanner('iconSize')} isTruePrivate={isTruePrivate} />}
                {canDisplayClubShowBanner && isClubShow && <ClubShowBanner className={stylesHeightAndFontSizeAndPaddingDefault} style={{
        '--font-size': stylesBanner('fontSize') + 'px',
        '--height': stylesBanner('height') + 'px'
      }} iconSize={stylesBanner('iconSize')} />}
                {canDisplayNewModelBanner && <NewModelBanner className={stylesNewModelBanner} style={{
        '--font-size': stylesBanner('fontSize') + 'px',
        '--height': stylesBanner('height') + 'px'
      }} timeClassName={stylesMargin} timeStyle={{
        '--margin': stylesBannerNewTime('margin')
      }} compact={columnWidth < 135} creation={creation} />}
            </div>
            <div style={{
      '--height': stylesSection1('height') + 'px'
    } as CSSProperties} className={cx(statusStyles, canDisplayStatusBadge && stylesVisibility)}>
                {!!canDisplayStatusBadge && <StatusBadge style={{
        '--font-size': stylesBadgeStatus('fontSize') + 'px'
      }} className={stylesStatusBadge} status={modelStatus} />}
            </div>
            <div style={{
      '--height': stylesFooter('height') + 'px'
    } as CSSProperties} className={cx(stylesBadgeContainer, (canDisplayBadges || canShowMenu) && stylesVisibility)}>
                <div style={{
        '--margin-bottom': stylesBadgeStatus('offsetY') + 'px',
        '--margin-left': stylesBadgeStatus('offsetX') + 'px'
      } as CSSProperties} className={stylesBadgesContainer}>
                    {(candisplayFriendBadge || canDisplayBookmarkBadge || canDisplayTrendingBadge || canDisplayPopularBadge || canDisplayPriorMissMFCBadge || canDisplayRecommendationBadge) && <div className={cx(stylesBadges, badgeCount === 1 && stylesFlexStart)}>
                            {candisplayFriendBadge && <FriendBadge size={stylesBadgeStatus('iconSize')} shadow />}
                            {canDisplayBookmarkBadge && <BookmarkBadge size={stylesBadgeStatus('iconSize')} shadow />}
                            {canDisplayRecommendationBadge && <RecommendationBadge size={stylesBadgeStatus('iconSize')} shadow />}
                            {canDisplayPopularBadge && <PopularBadge size={stylesBadgeStatus('iconSize')} shadow />}
                            {canDisplayTrendingBadge && <TrendingBadge size={stylesBadgeStatus('iconSize')} shadow />}
                            {canDisplayPriorMissMFCBadge && <PriorMissMFCBadge className={stylesPriorMissMFCBadgeClass} style={{
            '--height': stylesPriorMissMFCBadge('height') + 'px',
            '--width': stylesPriorMissMFCBadge('width') + 'px'
          }} containerProps={{
            styleOverrides: {
              display: 'flex',
              alignItems: 'center'
            }
          }} priorMissMfcRank={priorMissMfcRank} shadow />}
                        </div>}
                    {canDisplayMissMFCBadge && <div className={stylesDisplayMissMFCBadge}>
                            <div style={{
            '--font-size': stylesBadgeStatus('fontSize') + 'px'
          } as CSSProperties} className={cx(stylesMissMfcContainer, stylesBadgeMissMFC('compact') && stylesMissMfcContainerCompact)}>
                                <MissMFCBadge compact={stylesBadgeMissMFC('compact')} missMFCRank={missMFCRank} />
                            </div>
                        </div>}
                </div>
                {(canShowViewers || canShowMenu) && <div style={{
        '--margin-bottom': stylesBadgeStatus('offsetY') + 'px',
        '--margin-right': stylesBadgeStatus('offsetX') + 'px'
      } as CSSProperties} className={cx(stylesBadgeContainer, stylesMenuViewers, stylesVisibility)}>
                        {canShowViewers && <div className={stylesViewerContainer}>
                                <IconViewers title={locales.modelTile.viewers} size={stylesViewers('iconSize')} color={palette.backgroundReverse} styles={{
            '--margin-bottom': stylesViewers('offsetY') + 'px'
          } as CSSProperties} />
                                <span style={{
            '--font-size': stylesViewers('fontSize') + 'px'
          } as CSSProperties} className={stylesViewerCounter}>
                                    {viewers}
                                </span>
                            </div>}
                        {canShowMenu && <ButtonModelMenu onClick={onOpenMenuCallback} />}
                    </div>}
                {/* <div
                    style={
                        {
                            '--height': stylesFooter('height') + 'px',
                        } as CSSProperties
                    }
                    className={cx(
                        stylesButtonModelMenu,
                        canShowMenu && stylesVisibility,
                    )}
                 >
                    <ButtonModelMenu
                        style={{
                            '--margin': stylesFooter('margin'),
                        }}
                        onClick={onOpenMenuCallback}
                        className={stylesMargin}
                    />
                 </div> */}
            </div>
        </div>;
});
export default Text;
export { Text };